<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from '@/components/validations'
import Games from "@/services/Games";

export default {
  components:{
    validationMessages
  },
  props: {
    questionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedLanguage: '',
      translatedQuestion: '',
      translatedAnswers: ['', '', '', ''],
      submitted: false,
      showModal: false,
      tryingToSubmit: false,
      availableLanguages: [
        { value: 'ar', text: 'Arabic (AR)' },
        { value: 'en', text: 'English (EN)' },
        { value: 'nl', text: 'Dutch (NL)' },
        { value: 'fr', text: 'French (FR)' },
        { value: 'es', text: 'Spanish (ES)' },
        { value: 'de', text: 'German (DE)' },
        { value: 'it', text: 'Italian (IT)' },
        { value: 'pt', text: 'Portuguese (PT)' },
        { value: 'ru', text: 'Russian (RU)' },
        { value: 'zh', text: 'Chinese (ZH)' },
        { value: 'ja', text: 'Japanese (JA)' }
      ]
    };
  },
  validations: {
    selectedLanguage: {
      required
    },
    translatedQuestion: {
      required
    }
  },
  methods: {
    async translateQuestion(){
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      try {
        // Prepare answers array (only include filled answers)
        const answers = [];
        this.questionData.answers.forEach((answer, index) => {
          if (this.translatedAnswers[index] && this.translatedAnswers[index].trim() !== '') {
            answers.push({
              answer_id: answer.id,
              answer: this.translatedAnswers[index].trim()
            });
          }
        });

        // Send question and all answers in ONE request
        const translationData = {
          question_id: this.questionData.id,
          language: this.selectedLanguage,
          question: this.translatedQuestion,
          answers: answers  // Array of answers
        };

        await Games.editQuizQuestionTranslation(translationData);

        this.successmsg(`Successfully added ${this.selectedLanguage.toUpperCase()} translation for question and answers`);
        this.closeModal();
        this.refreshData();
        this.tryingToSubmit = false;

      } catch (error) {
        let errorMessage = "An unexpected error occurred";

        if (error.response && error.response.data && error.response.data.error) {
          if (Array.isArray(error.response.data.error)) {
            errorMessage = error.response.data.error.join(', ');
          } else {
            errorMessage = error.response.data.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.request) {
          errorMessage = "No response received from server";
        }

        this.failedmsg(errorMessage);
        console.error("Quiz translation error:", error);
        this.tryingToSubmit = false;
      }
    },

    refreshData() {
      this.$emit('onRefresh');
    },

    closeModal() {
      this.showModal = false;
      this.resetProps();
    },

    resetProps(){
      this.selectedLanguage = '';
      this.translatedQuestion = '';
      this.translatedAnswers = ['', '', '', ''];
      this.submitted = false;
      this.$v.$reset();
    },

    successmsg(message) {
      this.$bvToast.toast(message, {
        title: 'Success',
        variant: 'success',
        solid: true
      });
    },

    failedmsg(message) {
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        solid: true
      });
    }
  },
};
</script>

<template>
  <b-modal v-model="showModal" @hidden="resetProps" id="translate_question_modal" title="Translate Question & Answers" title-class="font-18" size="xl" centered>
    <form @submit.prevent="translateQuestion">
      <div class="row">
        <!-- Language Selector -->
        <div class="col-12">
          <b-form-group label-for="language-selector" class="mb-3">
            <label for="language">Select Language <span class="text-danger">*</span></label>
            <b-form-select
                v-model="selectedLanguage"
                :options="availableLanguages"
                id="language"
                :class="{
                  'is-invalid': submitted && $v.selectedLanguage.$error,
                }"
            >
              <template #first>
                <b-form-select-option :value="''" disabled>-- Please select a language --</b-form-select-option>
              </template>
            </b-form-select>
            <validationMessages v-if="submitted" :fieldName="'Language'" :validationName="$v.selectedLanguage"></validationMessages>
          </b-form-group>
        </div>

        <hr />

        <!-- Question Section -->
        <div class="col-12">
          <h5 class="mb-3">Question Translation</h5>
        </div>

        <!-- Display Original Question -->
        <div class="col-12 mb-3">
          <label class="form-label"><strong>Original Question (EN):</strong></label>
          <div class="alert alert-light mb-2">
            <p class="mb-0">{{ questionData.question }}</p>
          </div>
        </div>

        <!-- Translated Question Input -->
        <div class="col-12">
          <b-form-group label-for="translated-question-input" class="mb-3">
            <label for="translatedQuestion">Translated Question <span class="text-danger">*</span></label>
            <b-form-textarea
                v-model.trim="translatedQuestion"
                id="translatedQuestion"
                rows="3"
                placeholder="Enter the translated question..."
                :class="{
                  'is-invalid': submitted && $v.translatedQuestion.$error,
                }"
            >
            </b-form-textarea>
            <validationMessages v-if="submitted" :fieldName="'Translated Question'" :validationName="$v.translatedQuestion"></validationMessages>
          </b-form-group>
        </div>

        <hr />

        <!-- Answers Section -->
        <div class="col-12">
          <h5 class="mb-3">Answers Translation</h5>
        </div>

        <div class="col-12" v-if="questionData.answers">
          <div v-for="(answer, index) in questionData.answers" :key="answer.id" class="mb-3">
            <label class="form-label">
              <strong>Answer {{ index + 1 }}:</strong>
              <span class="text-muted ms-2">{{ answer.answer }}</span>
            </label>
            <b-form-input
                v-model.trim="translatedAnswers[index]"
                type="text"
                :id="'translated-answer-' + index"
                :placeholder="'Enter translated answer ' + (index + 1) + '...'"
            >
            </b-form-input>
          </div>
        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="translateQuestion" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save Translation
      </b-button>
    </template>
  </b-modal>
</template>

